import { useEffect, useState } from 'react';
import { api } from '../api';
import { formatEuros, shortDate } from '../format';
import type { Dashboard, Session } from '../types';
import { ComptabilitePanel } from './ComptabilitePanel';
import { ConfigPanel } from './ConfigPanel';
import { DepositsPanel } from './DepositsPanel';
import { FinancesPanel } from './FinancesPanel';
import { InspectionsPanel } from './InspectionsPanel';
import { LeasesPanel } from './LeasesPanel';
import { MaintenancePanel } from './MaintenancePanel';
import { PeoplePanel } from './PeoplePanel';
import { RoomsPanel } from './RoomsPanel';

type Tab = 'overview' | 'rooms' | 'people' | 'leases' | 'inspections' | 'maintenance' | 'finances' | 'comptabilite' | 'deposits' | 'config';

const TABS: Array<{ id: Tab; label: string }> = [
  { id: 'overview', label: 'Tableau de bord' },
  { id: 'rooms', label: 'Chambres' },
  { id: 'people', label: 'Locataires' },
  { id: 'leases', label: 'Baux' },
  { id: 'inspections', label: 'États des lieux' },
  { id: 'maintenance', label: 'Entretien' },
  { id: 'finances', label: 'Finances' },
  { id: 'comptabilite', label: 'Comptabilité' },
  { id: 'deposits', label: 'Caution' },
  { id: 'config', label: 'Configuration' },
];

const EVENT_LABELS: Record<string, string> = {
  LEASE_END: 'Fin de bail', LEASE_START: 'Entrée', INSPECTION: 'État des lieux',
  IRL_REVIEW: 'Révision IRL', DPE_EXPIRY: 'DPE', TENANT_MAJORITY: 'Majorité',
};

const MONTHS = [
  'janvier', 'février', 'mars', 'avril', 'mai', 'juin',
  'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre',
];

function monthLabel(month: string): string {
  const index = Number(month.slice(5, 7)) - 1;
  return `${MONTHS[index] ?? month} ${month.slice(0, 4)}`;
}

function OverviewTab({ dashboard }: { dashboard: Dashboard }) {
  const { overdue, cash, cashflow, occupancy, upcoming } = dashboard;
  return (
    <div className="overview-grid">
      {/* L'argent qui manque en premier : c'est le seul poste qui demande une
          action le jour même. */}
      <section className="panel kpi-panel">
        <div className="kpi-grid">
          <div className={overdue.totalCents > 0 ? 'kpi alert' : 'kpi'}>
            <dt>Impayés</dt>
            <dd>{formatEuros(overdue.totalCents)}</dd>
            <p>{overdue.count === 0
              ? 'Tout est encaissé.'
              : `${overdue.count} échéance(s), la plus ancienne depuis ${overdue.oldestDays} jour(s).`}</p>
          </div>
          <div className="kpi">
            <dt>Trésorerie générée</dt>
            <dd>{formatEuros(cash.generatedCents)}</dd>
            <p>Depuis le premier jour, loyers encaissés moins dépenses. Hors dépôts.</p>
          </div>
          <div className="kpi">
            <dt>Dépôts détenus</dt>
            <dd>{formatEuros(cash.depositsHeldCents)}</dd>
            <p>Sur le compte, mais dus aux locataires. À ne pas dépenser.</p>
          </div>
          <div className="kpi">
            <dt>Occupation</dt>
            <dd>{occupancy.rate === null ? '—' : `${occupancy.rate} %`}</dd>
            <p>{occupancy.occupiedCount}/{occupancy.roomCount} chambres occupées aujourd'hui.</p>
          </div>
          <div className={cashflow.netCents < 0 ? 'kpi alert' : 'kpi'}>
            <dt>Cashflow de {monthLabel(cashflow.month)}</dt>
            <dd>{formatEuros(cashflow.netCents)}</dd>
            <p>{formatEuros(cashflow.rentCents)} de loyers encaissés moins {formatEuros(cashflow.expensesCents)} de charges et dépenses du mois.</p>
          </div>
        </div>
      </section>

      {(upcoming.length > 0 || dashboard.alerts.length > 0) && (
        <div className="overview-split">
          {upcoming.length > 0 && (
            <section>
              <h2>Ce qui arrive · 90 prochains jours</h2>
              <ul className="timeline">
                {upcoming.map((event, index) => (
                  <li key={`${event.date}-${index}`}>
                    <span className="timeline-when">
                      {event.daysAway === 0 ? "aujourd'hui" : `dans ${event.daysAway} j`}
                    </span>
                    <span className="timeline-what">
                      <strong>{EVENT_LABELS[event.kind] ?? event.kind}</strong>
                      {event.label}{event.roomName && ` · ${event.roomName}`}
                    </span>
                    <span className="muted">{shortDate(event.date)}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}
          {dashboard.alerts.length > 0 && (
            <section>
              <h2>À surveiller</h2>
              <ul className="alert-list">
                {dashboard.alerts.map((alert, index) => <li key={index} className={alert.level}>{alert.message}</li>)}
              </ul>
            </section>
          )}
        </div>
      )}

      {overdue.lines.length > 0 && (
        <section className="panel">
          <p className="eyebrow">À relancer</p>
          <h2>Échéances impayées</h2>
          <div className="record-list">
            {overdue.lines.map((unpaid, index) => (
              <article className="record-card" key={`${unpaid.leaseId}-${unpaid.period}-${index}`}>
                <div className="record-heading">
                  <div>
                    <strong>{unpaid.roomName} · {unpaid.tenantName}</strong>
                    <span className="record-code">{unpaid.period ?? 'sans période'}</span>
                  </div>
                  <strong className="warn-text">{formatEuros(unpaid.amountCents - unpaid.paidCents)}</strong>
                </div>
                <p className="record-line muted">
                  <span>échue le {shortDate(unpaid.dueDate)}</span>
                  <span>{formatEuros(unpaid.paidCents)} reçu sur {formatEuros(unpaid.amountCents)}</span>
                </p>
              </article>
            ))}
          </div>
        </section>
      )}

      <section className="panel">
        <p className="eyebrow">Occupation</p>
        <h2>Les {dashboard.rooms.length} chambres</h2>
        <div className="record-list">
          {dashboard.rooms.map((room) => (
            <article className="record-card" key={room.id}>
              <div className="record-heading">
                <div><strong>{room.name}</strong>{room.hasBalcony && <span className="record-code">balcon</span>}</div>
                <span className={`status ${room.occupied ? 'signed' : 'draft'}`}>{room.occupied ? 'Occupée' : 'Libre'}</span>
              </div>
              {room.occupied && <p className="record-line">{room.tenantName}{room.leaseEndsOn ? ` · jusqu'au ${shortDate(room.leaseEndsOn)}` : ''}</p>}
              {!room.occupied && room.nextTenantName && (
                <p className="record-line muted">
                  {room.nextLeaseIsDraft ? 'Brouillon en préparation' : 'Prochain'} : {room.nextTenantName}
                  {room.nextEffectiveFrom ? ` à partir du ${shortDate(room.nextEffectiveFrom)}` : ''}
                </p>
              )}
              {!room.occupied && !room.nextTenantName && <p className="record-line muted">Aucun bail en préparation.</p>}
            </article>
          ))}
        </div>
      </section>
    </div>
  );
}

export function AdminDashboard({ session, onLogout }: {
  session: Extract<Session, { authenticated: true }>;
  onLogout: () => Promise<void>;
}) {
  const [dashboard, setDashboard] = useState<Dashboard | null>(null);
  const [error, setError] = useState('');
  const [tab, setTab] = useState<Tab>('overview');

  async function load(): Promise<void> {
    try {
      const result = await api<Dashboard>('/api/admin/dashboard');
      setDashboard(result);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Chargement impossible.');
    }
  }

  useEffect(() => { void load(); }, []);

  return (
    <div className="admin-shell">
      <aside className="admin-side">
        <div className="admin-brand">
          <p className="brand-kicker">Coloc</p>
          <p className="admin-brand-name">Martigues</p>
        </div>
        <nav className="side-nav" aria-label="Sections de l'administration">
          {TABS.map((entry) => <button key={entry.id} className={tab === entry.id ? 'active' : ''} onClick={() => setTab(entry.id)}>{entry.label}</button>)}
        </nav>
        <div className="admin-side-foot">
          <p>Bonjour {session.displayName}</p>
          <button className="text-button" onClick={() => void onLogout()}>Déconnexion</button>
        </div>
      </aside>
      {/* Le livre comptable est le seul écran dont le contenu (jusqu'à 13
          colonnes mensuelles) dépasse naturellement les 1100px de confort de
          lecture des autres onglets : élargi seulement pour lui, plutôt que
          de faire perdre à tous les autres écrans leur largeur resserrée. */}
      <main className={tab === 'comptabilite' ? 'admin-main wide' : 'admin-main'}>
        <h1 className="admin-title">{TABS.find((entry) => entry.id === tab)?.label ?? ''}</h1>
        {error && <div className="inline-message error" role="alert">{error}</div>}
        {!dashboard && !error && <p className="muted">Chargement…</p>}
        {dashboard && tab === 'overview' && <OverviewTab dashboard={dashboard} />}
        {tab === 'rooms' && <RoomsPanel />}
        {tab === 'people' && <PeoplePanel />}
        {tab === 'leases' && <LeasesPanel />}
        {dashboard && tab === 'inspections' && <InspectionsPanel dashboard={dashboard} onReload={load} />}
        {tab === 'maintenance' && <MaintenancePanel />}
        {tab === 'finances' && <FinancesPanel />}
        {tab === 'comptabilite' && <ComptabilitePanel />}
        {tab === 'deposits' && <DepositsPanel />}
        {tab === 'config' && <ConfigPanel />}
      </main>
    </div>
  );
}
